import { useEffect } from 'react'
import Cookies from 'js-cookie'
import { createFileRoute, Outlet, useNavigate } from '@tanstack/react-router'
import { cn } from '@/lib/utils'
import { SearchProvider } from '@/context/search-context'
import { SidebarProvider } from '@/components/ui/sidebar'
import { AppSidebar } from '@/components/layout/app-sidebar'
import SkipToMain from '@/components/skip-to-main'
import { useAuthStore } from '@/stores/authStore'
import { ServiceHealthGate } from '@/features/health/components/ServiceHealthGate'
import { PublicProfileSheet } from '@/components/public-profile-sheet'
import { PasskeyEnrollPrompt } from '@/features/auth/passkey-prompt/PasskeyEnrollPrompt'

export const Route = createFileRoute('/_authenticated')({
  component: RouteComponent,
})

function RouteComponent() {
  const defaultOpen = Cookies.get('sidebar_state') !== 'false'
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const isLoading = useAuthStore((s) => s.isLoading)
  const restoreSession = useAuthStore((s) => s.restoreSession)
  const refreshToken = useAuthStore((s) => s.refreshToken)
  const navigate = useNavigate()

  useEffect(() => {
    restoreSession()
  }, [restoreSession])

  // Keep the Cognito ID token fresh. It expires ~hourly; without this the
  // store's token goes stale and every authenticated nest-api call 401s,
  // emptying the whole app (events, lists, teammates, profile). Refresh on a
  // short interval (getSession is a cheap no-op until the token actually needs
  // renewing) and whenever the tab regains focus, so a backgrounded tab is
  // current the moment the user returns.
  useEffect(() => {
    if (!isAuthenticated) return
    refreshToken()
    const id = window.setInterval(refreshToken, 4 * 60 * 1000)
    const onFocus = () => {
      if (document.visibilityState === 'visible') refreshToken()
    }
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onFocus)
    return () => {
      window.clearInterval(id)
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onFocus)
    }
  }, [isAuthenticated, refreshToken])

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate({ to: '/sign-in' })
    }
  }, [isLoading, isAuthenticated, navigate])

  if (isLoading) {
    return (
      <div className='flex h-svh items-center justify-center'>
        <div className='text-muted-foreground text-sm'>Loading...</div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return null
  }

  return (
    <ServiceHealthGate>
      <SearchProvider>
        <SidebarProvider defaultOpen={defaultOpen}>
          <SkipToMain />
          <AppSidebar />
          <div
            id='content'
            className={cn(
              'ml-auto w-full max-w-full',
              'peer-data-[state=collapsed]:w-[calc(100%-var(--sidebar-width-icon)-1rem)]',
              'peer-data-[state=expanded]:w-[calc(100%-var(--sidebar-width))]',
              'sm:transition-[width] sm:duration-200 sm:ease-linear',
              'flex h-svh flex-col',
              'group-data-[scroll-locked=1]/body:h-full',
              'has-[main.fixed-main]:group-data-[scroll-locked=1]/body:h-svh'
            )}
          >
            <Outlet />
            <PublicProfileSheet />
            <PasskeyEnrollPrompt />
          </div>
        </SidebarProvider>
      </SearchProvider>
    </ServiceHealthGate>
  )
}
